'use client'

import { useState, useEffect, forwardRef, useImperativeHandle, DragEvent } from 'react'
import { useTranslations } from 'next-intl'
import { PlusCircleIcon, GripVerticalIcon, TrashIcon } from 'lucide-react'
import Flag from 'react-flagkit'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { FormLabel } from '@/components/ui/form'
import { PropertyFieldInput } from '@/types/property'
import { FieldTypeEnum } from '@/enums'

type PropsType = {
  fields: PropertyFieldInput[]
  locales: { fullCode: string; code: string; name: string }[]
  onChange: (fields: PropertyFieldInput[]) => void
  onRemoveField: (fieldId: string) => void
  validationErrors?: Record<string, string>
}

export type FieldsManagerRef = {
  handleAddField: () => void
}

const FieldsManager = forwardRef<FieldsManagerRef, PropsType>(
  ({ fields, locales, onChange, onRemoveField, validationErrors = {} }, ref) => {
    const t = useTranslations()
    const [localFields, setLocalFields] = useState<PropertyFieldInput[]>([])
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null)

    // Function to check if a field has validation errors
    const hasFieldError = (fieldIndex: number, locale: string) => {
      const errorKey = `fields.${fieldIndex}.translations.${locale}.name`
      return !!validationErrors[errorKey]
    }

    // Function to get the error property for a field
    const getFieldErrorMessage = (fieldIndex: number, locale: string) => {
      const errorKey = `fields.${fieldIndex}.translations.${locale}.name`
      return validationErrors[errorKey]
    }

    useEffect(() => {
      setLocalFields(fields)
    }, [fields])

    const getCountryCodeFromLocale = (localeCode: string): string => {
      const parts = localeCode.split('-')
      return parts.length > 1 ? parts[1] : parts[0].toUpperCase()
    }

    const handleAddField = () => {
      const newField: PropertyFieldInput = {
        // New fields should not have a UUID
        type: FieldTypeEnum.STRING,
        order: localFields.length + 1,
        required: false,
        translations: {}
      }

      // Initialize translations for all locales
      locales.forEach(locale => {
        newField.translations[locale.fullCode] = {
          name: ''
        }
      })

      const updatedFields = [...localFields, newField]
      setLocalFields(updatedFields)
      onChange(updatedFields)

      // After adding a new field, focus the first input in the default locale tab
      const newIndex = updatedFields.length - 1
      const defaultLocale = locales[0]?.fullCode
      if (defaultLocale) {
        setTimeout(() => {
          const input = document.getElementById(`field-${newIndex}-${defaultLocale}-name`) as HTMLInputElement | null
          if (input) {
            input.focus()
            input.select?.()
          }
        }, 100)
      }
    }

    // Expose handleAddField method to parent component
    useImperativeHandle(ref, () => ({
      handleAddField
    }))

    const handleRemoveField = (index: number) => {
      const fieldToRemove = localFields[index]
      const updatedFields = localFields
        .filter((_, i) => i !== index)
        .map((field, i) => ({
          ...field,
          order: i + 1
        }))

      setLocalFields(updatedFields)
      onChange(updatedFields)

      // If the field has an ID, notify parent to add it to removeFieldIds
      if (fieldToRemove.id && !fieldToRemove.id.includes('-')) {
        onRemoveField(fieldToRemove.id)
      }
    }

    const handleFieldChange = (index: number, field: Partial<PropertyFieldInput>) => {
      const updatedFields = [...localFields]
      updatedFields[index] = { ...updatedFields[index], ...field }
      setLocalFields(updatedFields)
      onChange(updatedFields)
    }

    const handleTranslationChange = (index: number, locale: string, name: string) => {
      const updatedFields = [...localFields]
      updatedFields[index] = {
        ...updatedFields[index],
        translations: {
          ...updatedFields[index].translations,
          [locale]: {
            name
          }
        }
      }
      setLocalFields(updatedFields)
      onChange(updatedFields)
    }

    const handleDragStart = (e: DragEvent<HTMLDivElement>, index: number) => {
      setDraggedIndex(index)
      e.dataTransfer.effectAllowed = 'move'
      // Required for Firefox
      e.dataTransfer.setData('text/plain', index.toString())
    }

    const handleDragOver = (e: DragEvent<HTMLDivElement>, index: number) => {
      e.preventDefault()
      if (draggedIndex === null || draggedIndex === index) {
        return
      }

      const updatedFields = [...localFields]
      const draggedField = updatedFields[draggedIndex]

      // Remove the dragged item
      updatedFields.splice(draggedIndex, 1)
      // Insert it at the new position
      updatedFields.splice(index, 0, draggedField)

      // Update order for all fields
      updatedFields.forEach((field, i) => {
        field.order = i + 1
      })

      setLocalFields(updatedFields)
      setDraggedIndex(index)
    }

    const handleDragEnd = () => {
      setDraggedIndex(null)
      onChange(localFields)
    }

    return (
      <div className="space-y-4">
        {localFields.map((field, index) => (
          <Card
            key={field.id || index}
            className="relative"
            draggable
            onDragStart={e => handleDragStart(e, index)}
            onDragOver={e => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}>
            <div className="absolute top-2 left-2 cursor-move">
              <GripVerticalIcon className="text-muted-foreground h-5 w-5" />
            </div>
            <CardContent className="pt-6">
              <div className="flex flex-row items-center justify-between">
                <div className="flex flex-row items-center justify-start justify-items-center space-x-4">
                  <div className="space-y-2">
                    <FormLabel required htmlFor={`field-${index}-type`}>
                      {t('type')}
                    </FormLabel>
                    <Select
                      value={field.type}
                      onValueChange={value => handleFieldChange(index, { type: value as FieldTypeEnum })}>
                      <SelectTrigger id={`field-${index}-type`} className="min-w-[120px]">
                        <SelectValue placeholder={t('select')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={FieldTypeEnum.STRING}>{t('field_type_string')}</SelectItem>
                        <SelectItem value={FieldTypeEnum.TEXT}>{t('field_type_text')}</SelectItem>
                        <SelectItem value={FieldTypeEnum.INTEGER}>{t('field_type_integer')}</SelectItem>
                        <SelectItem value={FieldTypeEnum.FLOAT}>{t('field_type_float')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="mt-2.5 flex items-center space-x-2">
                    <Switch
                      id={`field-${index}-required`}
                      checked={field.required}
                      onCheckedChange={checked => handleFieldChange(index, { required: checked })}
                    />
                    <Label htmlFor={`field-${index}-required`}>{t('required')}</Label>
                  </div>
                </div>
                <Button type="button" variant="destructive" size="sm" onClick={() => handleRemoveField(index)}>
                  <TrashIcon className="mr-2 h-4 w-4" />
                  {t('remove')}
                </Button>
              </div>

              <div className="mt-4">
                <Tabs
                  defaultValue={locales[0]?.fullCode}
                  onValueChange={value => {
                    // Focus the first input of the newly active tab
                    setTimeout(() => {
                      const input = document.getElementById(`field-${index}-${value}-name`) as HTMLInputElement | null
                      if (input) {
                        input.focus()
                        input.select?.()
                      }
                    }, 100)
                  }}>
                  <TabsList className="mb-2 w-full">
                    {locales.map(locale => {
                      const hasError = hasFieldError(index, locale.fullCode)
                      return (
                        <TabsTrigger
                          key={locale.fullCode}
                          value={locale.fullCode}
                          className={`relative flex items-center gap-2 ${hasError ? 'text-destructive border-destructive' : ''}`}>
                          <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={16} />
                          <span className="hidden sm:inline">{locale.name}</span>
                          {hasError && (
                            <div className="bg-destructive absolute -top-1 -right-1 h-2 w-2 animate-pulse rounded-full" />
                          )}
                        </TabsTrigger>
                      )
                    })}
                  </TabsList>

                  {locales.map(locale => (
                    <TabsContent key={locale.fullCode} value={locale.fullCode}>
                      <div className="flex items-center gap-2">
                        <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={20} />
                        <h4 className="text-sm font-semibold">{locale.name}</h4>
                        <Badge variant="outline" className="text-xs">
                          {locale.fullCode}
                        </Badge>
                      </div>

                      <div className="mt-2 space-y-2">
                        <FormLabel required htmlFor={`field-${index}-${locale.fullCode}-name`}>
                          {t('name')}
                        </FormLabel>
                        <Input
                          id={`field-${index}-${locale.fullCode}-name`}
                          value={field.translations[locale.fullCode]?.name || ''}
                          onChange={e => handleTranslationChange(index, locale.fullCode, e.target.value)}
                          placeholder={t('name')}
                          className={
                            hasFieldError(index, locale.fullCode) ? 'border-destructive focus:border-destructive' : ''
                          }
                        />
                        {hasFieldError(index, locale.fullCode) && (
                          <p className="text-destructive mt-1 text-sm font-medium">
                            {getFieldErrorMessage(index, locale.fullCode) || t('not_blank')}
                          </p>
                        )}
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            </CardContent>
          </Card>
        ))}

        {localFields.length === 0 && (
          <div className="rounded-md border border-dashed p-8 text-center">
            <p className="text-muted-foreground">{t('no_fields_added')}</p>
            <Button type="button" variant="outline" size="sm" className="mt-4" onClick={handleAddField}>
              <PlusCircleIcon className="h-4 w-4" />
              {t('add')}
            </Button>
          </div>
        )}
      </div>
    )
  }
)

FieldsManager.displayName = 'FieldsManager'

export default FieldsManager
